import React, { RefObject, useEffect, useRef } from 'react'

import { TIMELINE_PADDING_BOTTOM, TIMELINE_PADDING_TOP } from '../../constants'
import { BerthCard } from '../../types'
import { getInitialScrollTop } from '../../utils/timeConvert'
import { CardLayer } from '../CardLayer'
import { CurrentTimeLine } from '../CurrentTimeLine'
import { TimeLabels } from '../TimeLabels'
import { TimelineGrid } from '../TimelineGrid'

import styles from './index.module.css'

type Props = {
  cards: BerthCard[]
  date: string
  onCardTap: (cardId: string) => void
  pxPerMinute: number
  scrollRef: RefObject<HTMLDivElement>
  scrollTopBeforeScaleRef: React.MutableRefObject<number | null>
}

export const Timeline: React.FC<Props> = ({
  cards,
  date,
  onCardTap,
  pxPerMinute,
  scrollRef,
  scrollTopBeforeScaleRef,
}) => {
  const prevPxPerMinuteRef = useRef(pxPerMinute)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: getInitialScrollTop(new Date(), pxPerMinute) })
    }
    // 日付変更時のみ初期スクロール位置に戻す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  // 倍率変更時に画面中央の時刻を維持する
  useEffect(() => {
    const prev = prevPxPerMinuteRef.current
    prevPxPerMinuteRef.current = pxPerMinute
    if (prev === pxPerMinute) return
    const container = scrollRef.current
    if (!container) return
    const oldScrollTop = scrollTopBeforeScaleRef.current
    scrollTopBeforeScaleRef.current = null
    if (oldScrollTop === null) return

    const viewportHeight = container.clientHeight
    const centerMinutes = (oldScrollTop + viewportHeight / 2 - TIMELINE_PADDING_TOP) / prev
    const newScrollTop = centerMinutes * pxPerMinute + TIMELINE_PADDING_TOP - viewportHeight / 2
    const totalHeight = 24 * 60 * pxPerMinute + TIMELINE_PADDING_TOP + TIMELINE_PADDING_BOTTOM
    const maxScrollTop = Math.max(0, totalHeight - viewportHeight)
    container.scrollTop = Math.min(maxScrollTop, Math.max(0, newScrollTop))
  }, [pxPerMinute, scrollRef, scrollTopBeforeScaleRef])

  const timelineHeight = 24 * 60 * pxPerMinute + TIMELINE_PADDING_TOP + TIMELINE_PADDING_BOTTOM

  return (
    <div className={styles.scrollContainer} ref={scrollRef}>
      <div
        className={styles.body}
        style={{
          height: timelineHeight,
          paddingTop: TIMELINE_PADDING_TOP,
          paddingBottom: TIMELINE_PADDING_BOTTOM,
        }}
      >
        <TimeLabels pxPerMinute={pxPerMinute} />
        <div className={styles.gridArea}>
          <TimelineGrid pxPerMinute={pxPerMinute} />
          <CurrentTimeLine date={date} pxPerMinute={pxPerMinute} />
          <CardLayer cards={cards} onCardTap={onCardTap} pxPerMinute={pxPerMinute} />
        </div>
      </div>
    </div>
  )
}
